import React, { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import Swal from 'sweetalert2'
import Breadcrum from '../../components/breadcrum'
import AddItem from '../../components/addItem'
import { FaEdit, FaRegCopy, FaTrash } from 'react-icons/fa'
import { allSlider, deleteSlider, sendMail } from '../../api/sliderApi'
import { useNavigate } from 'react-router-dom'
import { handleDate } from '../../handleDateAndPdf'

const SliderTable = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [sliderToDelete, setSliderToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)

  useEffect(() => {
    document.title = 'Slider'
  }, [])

  // Fetch sliders using React Query
  const {
    data: sliders = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['sliders'],
    queryFn: allSlider,
    onError: (err) => {
      toast.error(err.message || 'Failed to load sliders')
    },
  })

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => sendMail(id),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setSliderToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setSliderToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) => deleteSlider(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch sliders
      queryClient.invalidateQueries({ queryKey: ['sliders'] })

      // Show success message
      toast.success('Slider deleted successfully!')

      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Deleted!',
        text: 'The slider has been deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setSliderToDelete(null)
    },
    onError: (err) => {
      console.error('Delete error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete slider. Please check your OTP.'
      toast.error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
        <p>Please enter the OTP sent to your email</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
          <small style="color: #1e40af;">
            <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
          </small>
        </div>
      `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Delete Slider',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Deleting...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: sliderToDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or deletion failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setSliderToDelete(null)
          toast.info('Deletion cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setSliderToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (sliderId, sliderTitle) => {
    setSliderToDelete(sliderId)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Delete Slider?',
      html: `
        <p>Are you sure you want to delete this slider?</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
          <strong style="color: #dc2626;">${sliderTitle}</strong>
        </div>
        <p style="color: #6b7280; font-size: 14px;">
          This action will send an OTP to your email for verification.
        </p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(sliderId)
      } else {
        setSliderToDelete(null)
      }
    })
  }

  // Handle copy link to clipboard with better feedback
  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link)
      toast.success('Link copied to clipboard!', {
        duration: 2000,
        icon: '📋',
      })
    } catch (err) {
      console.error('Failed to copy link:', err)
      toast.error('Failed to copy link')
    }
  }

  // Handle edit slider
  const handleEditSlider = (id) => {
    navigate(`/dealdashboard/add/slider?id=${id}`)
  }

  return (
    <>
      <Breadcrum
        heading="Sliders"
        subheading="Slider"
        link="/dealdashboard/slider"
      />

      <AddItem redirect="/dealdashboard/add/slider" />

      <div className="p-6 bg-white rounded shadow sm:w-[90%] mx-auto mt-8">
        <h2 className="text-2xl font-bold mb-4 text-center">
          Slider Management
        </h2>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
            <p className="mt-2 text-gray-500">Loading sliders...</p>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="text-center py-8 text-red-500">
            <p className="text-lg font-semibold">Error loading sliders</p>
            <p className="text-sm mt-1">
              {error?.message || 'Please try again later'}
            </p>
            <button
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['sliders'] })
              }
              className="mt-3 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && (!sliders || sliders.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-lg mb-2">No sliders found</p>
            <p className="text-sm">Add a new slider to get started</p>
          </div>
        )}

        {/* Responsive Table Wrapper */}
        {!isLoading && !isError && sliders && sliders.length > 0 && (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Total Sliders:{' '}
              <span className="font-semibold">{sliders.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full border-2 text-sm text-left whitespace-nowrap">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="p-3 font-semibold">Slider</th>
                    <th className="p-3 font-semibold">Heading</th>
                    <th className="p-3 font-semibold">Created At</th>
                    <th className="p-3 font-semibold">Created By</th>
                    <th className="p-3 font-semibold">Updated At</th>
                    <th className="p-3 font-semibold">Updated By</th>
                    <th className="p-3 font-semibold">Text</th>
                    <th className="p-3 font-semibold">Page Link</th>
                    <th className="p-3 font-semibold text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sliders.map((item, index) => (
                    <tr
                      key={item.id || index}
                      className="odd:bg-white even:bg-gray-50 border-b text-left hover:bg-gray-100 transition-colors"
                    >
                      <td className="p-3">
                        <img
                          src={item.imageUrl || '/placeholder-image.jpg'}
                          alt={item.title || 'Slider'}
                          className="w-24 h-20 object-cover rounded-lg shadow-sm border"
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = '/placeholder-image.jpg'
                          }}
                        />
                      </td>
                      <td className="p-3 text-sm font-medium max-w-xs">
                        <div className="truncate" title={item.title}>
                          {item.title}
                        </div>
                      </td>

                      <td className="p-3 text-sm font-medium min-w-[140px]">
                        {handleDate(item.createdAt)}
                      </td>
                      <td className="p-3 text-sm font-medium min-w-[120px]">
                        {item?.createAdmin || 'N/A'}
                      </td>
                      <td className="p-3 text-sm font-medium min-w-[140px]">
                        {item.updatedAt ? handleDate(item.updatedAt) : 'N/A'}
                      </td>
                      <td className="p-3 text-sm font-medium min-w-[120px]">
                        {item?.updateAdmin || 'N/A'}
                      </td>

                      <td className="p-3 text-sm text-gray-700 max-w-sm">
                        <div className="truncate" title={item.description}>
                          {item.description}
                        </div>
                      </td>

                      <td className="p-3 text-sm text-gray-500 max-w-xs">
                        <div className="flex items-center space-x-2">
                          <span className="truncate" title={item.pageUrl}>
                            {item.pageUrl}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopyLink(item.pageUrl)}
                            className="text-gray-400 hover:text-orange-500 transition-colors flex-shrink-0"
                            title="Copy Link"
                          >
                            <FaRegCopy />
                          </button>
                        </div>
                      </td>

                      <td className="p-3">
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={() => handleEditSlider(item.id)}
                            className="text-blue-500 hover:text-blue-700 transition-colors p-1"
                            title="Edit Slider"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteClick(item.id, item.title)
                            }
                            className={`text-red-500 hover:text-red-700 transition-colors p-1 ${
                              sendingMail || deleteMutation.isPending
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                            title="Delete Slider"
                            disabled={sendingMail || deleteMutation.isPending}
                          >
                            {sendingMail && sliderToDelete === item.id ? (
                              <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                            ) : (
                              <FaTrash />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Loading overlay for sending email */}
        {sendingMail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg font-semibold">
                Sending verification email...
              </p>
              <p className="text-sm text-gray-500 mt-1">Please wait</p>
            </div>
          </div>
        )}
      </div>
      <AddItem redirect="/dealdashboard/add/slider" />
    </>
  )
}

export default SliderTable
